import actions from '@src/redux/actions';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getBlockTimestampDuration,
  getTrimmedAudioDuration,
} from '@src/services/project';
import { getTrimmedAudioUrl } from '@src/services/trimmedAudio';

/**
 * Single-block audio preview for the video dubbing flow (US-14).
 *
 * Plays one block's audio on a dedicated element, cut to
 * MIN(block timestamp slot, audio duration) so the preview matches the block
 * timestamp. Split out of useVideoDubbingAudioPlayer to keep each engine small
 * and independently readable. Inert unless `enabled`.
 *
 * The cut is a property of the SOURCE, not of the timing: `services/trimmedAudio`
 * hands back a clip whose length already IS the played window, so the element
 * stops on its own and `ended` fires on the exact sample the block ends on.
 * Stopping a playing element instead — poll then `pause()` — lands a variable
 * amount early or late, because polling is at the mercy of the main thread and
 * `pause()` discards audio already rendered but not yet heard.
 *
 * When the exact clip cannot be prepared (no Web Audio, CORS, unsupported codec,
 * over the size caps) the raw file is played and the legacy watcher below trims
 * it — the exact pre-change behaviour, just less precise.
 */
export const useBlockAudioPreview = ({ enabled = false } = {}) => {
  const dispatch = useDispatch();
  const { isPlayingBlock, multiBlock, currentPlayingBlockId } = useSelector(
    (state) => state.project,
  );
  const { blocks } = multiBlock || {};

  const audioRef = useRef(new Audio());
  const prevLinkRef = useRef(null);
  // Timestamps of the block being previewed, read inside the watcher to trim.
  const timestampRef = useRef({ startTime: null, endTime: null });
  // The element holds an exactly-cut clip — the watcher must stay out of the way.
  const usingTrimmedRef = useRef(false);
  // Guards against an older source resolving after a newer one was requested.
  const loadSeqRef = useRef(0);
  // Whether playback is still wanted once the async source resolves.
  const wantPlayRef = useRef(false);
  // Legacy stop watcher, used ONLY on the fallback source. `timeupdate` fires
  // ~4x/s and a wall-clock timer drifts when the audio buffers, both leaking
  // audio past the block window; this reads the audio's OWN currentTime each
  // frame and halts on MIN(slot, fileDuration).
  const stopRafRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const cancelStopWatcher = useCallback(() => {
    if (stopRafRef.current) {
      cancelAnimationFrame(stopRafRef.current);
      stopRafRef.current = null;
    }
  }, []);

  const stopBlockPlayback = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    dispatch(actions.project.setIsPlayingBlock(false));
    setCurrentTime(0);
  }, [dispatch]);

  // Inert while the element holds an exactly-cut clip: that clip ends by itself.
  // Does NOT setState per frame — the seekbar is driven by the coarser
  // `timeupdate` so the (large) content tree isn't re-rendered at 60fps.
  const startStopWatcher = useCallback(() => {
    cancelStopWatcher();
    if (usingTrimmedRef.current) return;
    const audio = audioRef.current;
    const frame = () => {
      if (audio.paused) {
        stopRafRef.current = null;
        return;
      }
      const trimmed = getTrimmedAudioDuration({
        ...timestampRef.current,
        audioDuration: audio.duration,
      });
      if (trimmed && audio.currentTime >= trimmed) {
        stopBlockPlayback();
        stopRafRef.current = null;
        return;
      }
      stopRafRef.current = requestAnimationFrame(frame);
    };
    stopRafRef.current = requestAnimationFrame(frame);
  }, [cancelStopWatcher, stopBlockPlayback]);

  /**
   * Point the element at the block's audio, cut to its slot. `resumeAt` restores
   * the playback position after a re-cut (a live timestamp edit), applied once
   * the new clip's metadata is in.
   */
  const loadSource = useCallback(
    async ({ audioLink, startTime, endTime, resumeAt = 0 }) => {
      loadSeqRef.current += 1;
      const seq = loadSeqRef.current;
      const audio = audioRef.current;
      // The service clamps the cut to the file's own length, so passing the slot
      // yields MIN(slot, duration) without a metadata probe here.
      const slotSec = getBlockTimestampDuration({ startTime, endTime });
      const trimmedUrl = slotSec
        ? await getTrimmedAudioUrl(audioLink, slotSec)
        : null;
      // A newer block (or a newer cut) won the race — leave the element alone.
      if (seq !== loadSeqRef.current) return;

      usingTrimmedRef.current = Boolean(trimmedUrl);
      audio.src = trimmedUrl || audioLink;
      if (resumeAt > 0) {
        audio.addEventListener(
          'loadedmetadata',
          () => {
            audio.currentTime = Math.min(resumeAt, audio.duration || resumeAt);
          },
          { once: true },
        );
      }
      if (wantPlayRef.current) audio.play().catch(() => {});
    },
    [],
  );

  const handlePlayAudioInBlock = (blockId, blockAudioExpiredAt) => {
    const audioHasExpired =
      blockAudioExpiredAt && moment().isAfter(blockAudioExpiredAt);
    if (audioHasExpired) {
      dispatch(
        actions.project.updateBlockSynthesisInfo(blockId, { audioLink: null }),
      );
      return;
    }

    dispatch(actions.project.setIsMultiBlockMode(false));

    const block = blocks?.[blockId];
    const audioLink = block?.audioLink;
    timestampRef.current = {
      startTime: block?.startTime,
      endTime: block?.endTime,
    };
    // Reload (vs. resume) when this isn't already the active preview block or
    // its audio changed. Keyed off the redux `currentPlayingBlockId` rather than
    // a local ref: undo/redo's pauseAudio clears it, and a stale local ref would
    // then skip re-activating the block (popover/play state left out of sync).
    if (
      currentPlayingBlockId !== blockId ||
      prevLinkRef.current !== audioLink
    ) {
      prevLinkRef.current = audioLink;
      setCurrentTime(0);
      setDuration(0);
      dispatch(actions.project.setAudioLink(audioLink));
      dispatch(actions.project.setCurrentPlayingBlockId(blockId));
      wantPlayRef.current = true;
      loadSource({
        audioLink,
        startTime: block?.startTime,
        endTime: block?.endTime,
      });
    }
    dispatch(actions.project.setIsPlayingBlock(true));
    dispatch(actions.project.setIsPlaying(false));
  };

  // Play/pause the preview element in sync with `isPlayingBlock`.
  useEffect(() => {
    if (!enabled) return;
    const audio = audioRef.current;
    wantPlayRef.current = isPlayingBlock;
    if (isPlayingBlock) {
      audio
        .play()
        .then(startStopWatcher)
        .catch(() => {});
    } else {
      audio.pause();
      cancelStopWatcher();
    }
  }, [enabled, isPlayingBlock, startStopWatcher, cancelStopWatcher]);

  // Track progress/duration. On an exactly-cut clip the element ends itself, so
  // `timeupdate` only drives the seekbar (~4x/s, keeping the tree off a 60fps
  // render). On the fallback source it also backstops the rAF watcher for when
  // rAF is throttled (hidden tab).
  useEffect(() => {
    if (!enabled) return undefined;
    const audio = audioRef.current;
    const playedDuration = () =>
      usingTrimmedRef.current
        ? audio.duration || 0
        : getTrimmedAudioDuration({
            ...timestampRef.current,
            audioDuration: audio.duration,
          });
    const onEnded = () => {
      cancelStopWatcher();
      dispatch(actions.project.setIsPlayingBlock(false));
      setCurrentTime(0);
    };
    const onTimeUpdate = () => {
      if (!usingTrimmedRef.current) {
        const trimmed = playedDuration();
        if (trimmed && audio.currentTime >= trimmed) {
          cancelStopWatcher();
          audio.pause();
          audio.currentTime = 0;
          dispatch(actions.project.setIsPlayingBlock(false));
          setCurrentTime(0);
          return;
        }
      }
      setCurrentTime(audio.currentTime);
    };
    const onLoadedMetadata = () => {
      setDuration(playedDuration());
      startStopWatcher();
    };

    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [enabled, dispatch, cancelStopWatcher, startStopWatcher]);

  // Keep the preview in sync with LIVE edits to the previewed block's timestamps.
  // An exactly-cut clip carries its window in its length, so a new window means a
  // new clip — re-cutting is a byte-range slice of the cached PCM (sub-millisecond),
  // and the position is restored so the edit doesn't restart the preview. On the
  // fallback source only the trim bounds change; `loadedmetadata` would not fire
  // again on the same src, so the displayed duration is recomputed here.
  const previewBlock = blocks?.[currentPlayingBlockId];
  const previewStartTime = previewBlock?.startTime;
  const previewEndTime = previewBlock?.endTime;
  const previewLink = previewBlock?.audioLink;
  useEffect(() => {
    if (!enabled || (!previewStartTime && !previewEndTime)) return;
    timestampRef.current = {
      startTime: previewStartTime,
      endTime: previewEndTime,
    };
    const audio = audioRef.current;
    if (usingTrimmedRef.current) {
      loadSource({
        audioLink: previewLink,
        startTime: previewStartTime,
        endTime: previewEndTime,
        resumeAt: audio.currentTime,
      });
      return;
    }
    if (audio.duration) {
      setDuration(
        getTrimmedAudioDuration({
          startTime: previewStartTime,
          endTime: previewEndTime,
          audioDuration: audio.duration,
        }),
      );
    }
    // Re-arm the watcher for the new window (a shortened endTime must cut
    // sooner).
    startStopWatcher();
  }, [
    enabled,
    previewStartTime,
    previewEndTime,
    previewLink,
    loadSource,
    startStopWatcher,
  ]);

  const handleSeekedInBlock = (timeSec) => {
    audioRef.current.currentTime = timeSec;
    setCurrentTime(timeSec);
    startStopWatcher();
  };

  // Stop preview on unmount.
  useEffect(
    () => () => {
      if (stopRafRef.current) cancelAnimationFrame(stopRafRef.current);
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    },
    [],
  );

  const setVolume = (value) => {
    audioRef.current.volume = value / 100;
  };

  return {
    handlePlayAudioInBlock,
    handleSeekedInBlock,
    blockAudioCurrentTime: currentTime,
    blockAudioDuration: duration,
    setBlockVolume: setVolume,
  };
};

export default useBlockAudioPreview;
